import type { DocPage } from '../types'
import { media } from '../media'

export const logicalObjects: DocPage = {
  id: 'logical-objects',
  title: 'Logical objects',
  description:
    'Add, update, and remove logical collections, attributes, and enumerators to extend PostGIS and GeoPackage schemas for mapping and scratch transfers.',
  category: 'Workspace',
  sections: [
    { id: 'overview', title: 'What are logical objects?' },
    { id: 'when', title: 'When changes apply' },
    { id: 'collections', title: 'Logical collections' },
    { id: 'attributes', title: 'Logical attributes' },
    { id: 'types', title: 'Attribute types' },
    { id: 'enumerators', title: 'Enumerators (PostGIS)' },
    { id: 'menus', title: 'Context menu actions' },
    { id: 'scratch-fixed', title: 'Scratch and fixed values' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'overview',
      text: 'What are logical objects?',
    },
    {
      type: 'paragraph',
      text: 'Logical objects let you extend the loaded model with user-defined collections, attributes, and (on PostGIS) enumerators. Use them when the physical database or GeoPackage does not yet contain a table/field you need for mapping, or when preparing a scratch target schema shape.',
    },
    {
      type: 'table',
      headers: ['Object', 'Supported formats', 'Purpose'],
      rows: [
        [
          'Logical collection',
          'PostGIS; GeoPackage (collection name)',
          'Define a logical table/layer with database/schema/table or GPKG name and attributes.',
        ],
        [
          'Logical attribute',
          'PostGIS / GeoPackage contexts that allow logical attrs',
          'Add a typed field to a collection for mapping or scratch output.',
        ],
        [
          'Logical enumerator',
          'PostGIS only',
          'Define a PostgreSQL ENUM type and values for ENUM attributes.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'when',
      text: 'When changes apply',
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'Applied on the next transfer',
      text: 'Adding, updating, or removing logical objects updates the in-session model for mapping. Physical create/alter effects are applied when you run the next transfer that uses those logical definitions (especially scratch or write paths that materialize schema). The Add logical attribute dialog states this explicitly for PostGIS, for example: Changes apply in PostGIS on the next mapped transfer. Save your configuration if you want the logical setup to reload later.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'collections',
      text: 'Logical collections',
    },
    {
      type: 'paragraph',
      text: 'Use Add logical collection… / Update logical collection… / Remove logical collection… from the model context menu. The Add logical collection dialog lets you create a new table under an existing or new database and schema, and optionally define attributes now or on a later update.',
    },
    {
      type: 'figure',
      src: media.addLogicalCollectionModal,
      alt: 'Add logical collection dialog',
      caption:
        'Figure: Add logical collection — Database, Schema, Table, Define attributes, Import from source, and the dual panel to move source attributes into the new collection.',
    },
    {
      type: 'heading',
      level: 3,
      id: 'postgis-collection-fields',
      text: 'PostGIS fields',
    },
    {
      type: 'table',
      headers: ['Field / control', 'Description'],
      rows: [
        ['Database', 'Target database (for example aims_db); New database… when creating a new catalog name.'],
        ['Schema', 'Schema name (commonly public); New schema… when needed.'],
        ['Table', 'Physical/logical table name for the collection.'],
        [
          'Define attributes',
          'Optionally define columns on the right, move them from source on the left, or both.',
        ],
        [
          'Import from source',
          'Show source attributes alongside the collection list and move them left to right.',
        ],
        [
          'Source attributes (left)',
          'Select attributes from a source collection; items already moved show In collection.',
        ],
        [
          'Attributes (right)',
          'Attributes on the new collection; tagged (from source) when imported. Edit or remove with the pencil / X actions.',
        ],
        [
          'Deselect all / Remove selected / Return to source / Clear all',
          'Bulk manage which attributes stay on the new collection.',
        ],
        ['Add attribute', 'Create a brand-new typed attribute on the collection (not from source).'],
      ],
    },
    {
      type: 'heading',
      level: 3,
      id: 'gpkg-collection-fields',
      text: 'GeoPackage fields',
    },
    {
      type: 'table',
      headers: ['Field', 'Description'],
      rows: [
        ['Collection name', 'Name of the logical layer/table inside the GeoPackage.'],
        ['Define attributes', 'Typed attributes for the collection.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'attributes',
      text: 'Logical attributes',
    },
    {
      type: 'steps',
      items: [
        'Select a collection in the model tree.',
        'Choose Add logical attribute… (or Update / Remove for an existing logical attribute).',
        'Enter the attribute name and type.',
        'If the type is ENUM, select an enumerator type (PostGIS).',
        'Confirm with Add logical attribute — the field appears on the collection for mapping.',
      ],
    },
    {
      type: 'figure',
      src: media.addLogicalAttributeModal,
      alt: 'Add logical attribute dialog',
      caption:
        'Figure: Add logical attribute — Attribute name and Type (for example INTEGER) on a target collection such as aims_db.public.new_collection.',
      size: 'compact',
    },
    {
      type: 'table',
      headers: ['Field', 'Description'],
      rows: [
        ['Attribute name', 'Column name to add on the selected collection.'],
        ['Type', 'Data type from the supported list (STRING, INTEGER, DOUBLE, ENUM, geometry types, …).'],
        ['Enumerator', 'Required when Type is ENUM (PostGIS) — pick an existing enumerator type.'],
        ['Cancel', 'Close without adding.'],
        ['Add logical attribute', 'Confirm and attach the attribute to the collection.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'types',
      text: 'Attribute types',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Type', 'Typical use'],
      rows: [
        ['STRING', 'General text fields.'],
        ['INTEGER', '32-bit whole numbers.'],
        ['BIGINT', 'Large whole numbers.'],
        ['UNSIGNED_INT', 'Non-negative integers where required.'],
        ['FLOAT', 'Single-precision decimals.'],
        ['DOUBLE', 'Double-precision decimals.'],
        ['BOOLEAN', 'True/false flags.'],
        ['DATE', 'Calendar dates.'],
        ['TIME', 'Time-of-day values.'],
        ['DATETIME', 'Date and time.'],
        ['TIMESTAMP', 'Timestamp values.'],
        ['ENUM', 'Constrained value set — requires an enumerator (PostGIS).'],
        ['POINT', 'Point geometry attribute.'],
        ['LINE', 'Line geometry attribute.'],
        ['AREA', 'Area/polygon geometry attribute.'],
        ['GEOMETRY', 'Generic geometry.'],
        ['TEXT', 'Longer text / CAD text-oriented fields where offered.'],
      ],
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'ENUM requires an enumerator',
      text: 'When type is ENUM, you must Select an enumerator type for this attribute. If none exist, the UI shows No enumerators defined — add one first. Placeholder: Select enumerator type.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'enumerators',
      text: 'Enumerators (PostGIS)',
    },
    {
      type: 'paragraph',
      text: 'Enumerators are PostGIS-only. The modal describes: Add/Update a logical PostgreSQL ENUM type.',
    },
    {
      type: 'table',
      headers: ['Field', 'Description'],
      rows: [
        ['Schema', 'Schema that owns the ENUM type.'],
        ['Enum type name', 'PostgreSQL type name.'],
        ['Display name (optional)', 'Friendly label in the UI.'],
        ['Enum values', 'Ordered list of allowed values.'],
      ],
    },
    {
      type: 'paragraph',
      text: 'Primary action button: Add/Update logical enumerator. Use Remove logical enumerator… to delete a definition you no longer need (after confirming it is not required by attributes).',
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Screenshot tip',
      text: 'Capture Add-Logical-Enumerator-Modal.png when available to illustrate this section the same way as collection and attribute dialogs.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'menus',
      text: 'Context menu actions',
    },
    {
      type: 'list',
      items: [
        'Add logical collection… / Update logical collection… / Remove logical collection…',
        'Add logical attribute… / Update logical attribute… / Remove logical attribute…',
        'Add logical enumerator… / Update logical enumerator… / Remove logical enumerator… (PostGIS)',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'scratch-fixed',
      text: 'Scratch and fixed values',
    },
    {
      type: 'paragraph',
      text: 'On scratch transfers you cannot use value mapping, but you can Set fixed value for logical attributes from the mapping toolbar (F). Fixed values are applied after attribute mappings during transfer. See Fixed mapping and Mapping overview.',
    },
  ],
}
